import {
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    SecurityContext,
    computed,
    effect,
    forwardRef,
    inject,
    input,
    output,
    signal
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import { TiptapEditorDirective } from 'ngx-tiptap';
import { FileNameValidationOperations } from '../../util/file-name-validator.util';
import { SnackbarService } from '../sh-snackbar/snackbar.service';
import { ShEditorLinkMenuComponent } from './components/link-menu/sh-editor-link-menu.component';
import { ShEditorSpellcheckMenuComponent } from './components/spellcheck-menu/sh-editor-spellcheck-menu.component';
import { LinkMenuSubmission } from './models/sh-editor-overlay.model';
import { ToolbarCommand } from './models/sh-editor-prosemirror.model';
import { ShEditorSpellcheckExtension } from './plugins/sh-editor-spellcheck.plugin';
import { ShEditorStateService } from './services/sh-editor-state.service';
import { isMeaningfullyEmptyHtml, normalizeLegacyEditorHtml, sanitizePastedHtml } from './utils/sh-editor-paste.util';
import { SH_EDITOR_ICONS_DARK, SH_EDITOR_ICONS_LIGHT } from './utils/sh-editor-icons';

const DEFAULT_ACCEPTED_FILE_TYPES =
    'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.docm,application/zip,application/x-zip-compressed,.DOC,.DOCX,.XLS,.XLSX,.PPT,.PPTX,.TXT,.CSV,.HTML,.DOCM';
const DEFAULT_MIN_HEIGHT = '104px';
const DEFAULT_MAX_HEIGHT = '240px';
const DEFAULT_SPELLCHECK_DELAY = 1000;

@Component({
    selector: 'sh-editor',
    templateUrl: './sh-editor.component.html',
    imports: [
        TranslatePipe,
        TiptapEditorDirective,
        NgTemplateOutlet,
        ShEditorLinkMenuComponent,
        ShEditorSpellcheckMenuComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        ShEditorStateService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShEditorComponent),
            multi: true
        }
    ],
    host: {
        class: 'block'
    }
})
export class ShEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
    public readonly placeholder = input<string>('');
    public readonly theme = input<'auto' | 'dark' | 'light'>('auto');
    public readonly disabled = input<boolean>(false);
    public readonly showToolbar = input<boolean>(true);
    public readonly showBottomBar = input<boolean>(false);
    public readonly allowAttachments = input<boolean>(true);
    public readonly showAttachmentButton = input<boolean>(true);
    public readonly acceptedFileTypes = input<string>(DEFAULT_ACCEPTED_FILE_TYPES);
    public readonly minHeight = input<string>(DEFAULT_MIN_HEIGHT);
    public readonly maxHeight = input<string>(DEFAULT_MAX_HEIGHT);
    public readonly spellcheckDelay = input<number>(DEFAULT_SPELLCHECK_DELAY);
    public readonly label = input<string>('');
    public readonly required = input<boolean>(false);

    public readonly attachments = output<FileList>();
    public readonly spellingErrors = output<string[]>();
    public readonly valueChange = output<string>();

    /**
     * Tracks the global SHUI theme attribute (`<html theme="dark|light">`) so the
     * editor can pick a matching inline-SVG icon variant when the `theme` input
     * is set to `'auto'`. Updated by a `MutationObserver` set up in `ngOnInit`.
     */
    private readonly globalTheme = signal<'dark' | 'light'>(
        typeof document !== 'undefined' && document.documentElement.getAttribute('theme') === 'dark' ? 'dark' : 'light'
    );

    /**
     * Resolved theme used for picking the SVG icon variant. Honors an explicit
     * `theme` input (`'dark'` / `'light'`) and otherwise follows the global
     * SHUI theme attribute.
     */
    protected readonly resolvedTheme = computed<'dark' | 'light'>(() => {
        const explicit = this.theme();
        if (explicit === 'dark' || explicit === 'light') {
            return explicit;
        }
        return this.globalTheme();
    });

    /**
     * Inline SVG icons (as `url(...)` strings) for glyphs not in the SHUI icon
     * font. The `<sh-icon>` web component paints these via shadow-DOM
     * `background-image`, so we swap between pre-colored black/white variants
     * to follow the active theme rather than recoloring with a CSS filter.
     */
    protected readonly icons = computed(() =>
        this.resolvedTheme() === 'dark' ? SH_EDITOR_ICONS_DARK : SH_EDITOR_ICONS_LIGHT
    );

    private themeObserver?: MutationObserver;

    public editor!: Editor;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly editorStateService = inject(ShEditorStateService);
    private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly snackbarService = inject(SnackbarService);
    private readonly translateService = inject(TranslateService);

    public readonly canUndo = this.editorStateService.canUndo;
    public readonly canRedo = this.editorStateService.canRedo;
    public readonly canInsertLink = this.editorStateService.canInsertLink;
    public readonly activeFormats = this.editorStateService.activeFormats;
    public readonly spellcheckSuggestionMenu = this.editorStateService.spellcheckSuggestionMenu;
    public readonly linkMenu = this.editorStateService.linkMenu;

    private readonly formDisabled = signal(false);

    protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};
    private pendingValue = '';

    constructor() {
        effect(() => {
            if (this.editor) {
                this.editor.setEditable(!this.isDisabled());
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    public ngOnInit(): void {
        if (typeof document !== 'undefined' && typeof MutationObserver !== 'undefined') {
            this.themeObserver = new MutationObserver(() => {
                const theme = document.documentElement.getAttribute('theme') === 'dark' ? 'dark' : 'light';
                this.globalTheme.set(theme);
            });
            this.themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['theme']
            });
        }

        this.editorStateService.configure({
            getEditor: () => this.editor,
            getEditorValue: () => this.getEditorValue(),
            getSpellcheckDelay: () => this.spellcheckDelay(),
            isDisabled: () => this.isDisabled(),
            emitSpellingErrors: (errors) => this.spellingErrors.emit(errors),
            markForCheck: () => this.changeDetectorRef.markForCheck()
        });

        this.editor = new Editor({
            content: normalizeLegacyEditorHtml(this.pendingValue),
            editable: !this.isDisabled(),
            editorProps: {
                attributes: {
                    class: 'sh-editor-host sh-editor-content',
                    spellcheck: 'false',
                    translate: 'yes',
                    'data-testid': 'global-form-textarea-rich-text'
                },
                transformPastedHTML: sanitizePastedHtml,
                handleDrop: (_view, event) => {
                    if (!event.dataTransfer?.files?.length) {
                        return false;
                    }

                    if (!this.allowAttachments()) {
                        event.preventDefault();
                        return true;
                    }

                    event.preventDefault();
                    this.handleFiles(event.dataTransfer.files);

                    return true;
                },
                handleClick: (_view, _pos, event) => {
                    if (this.isDisabled()) {
                        return false;
                    }

                    const target = event.target;
                    if (!(target instanceof HTMLElement) || !target.closest('a')) {
                        return false;
                    }

                    event.preventDefault();
                    return true;
                }
            },
            extensions: [
                StarterKit,
                Underline,
                Link.configure({
                    openOnClick: false,
                    autolink: false,
                    HTMLAttributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
                }),
                Placeholder.configure({
                    placeholder: this.placeholder()
                }),
                Table.configure({
                    resizable: false
                }),
                TableRow,
                TableHeader,
                TableCell,
                ShEditorSpellcheckExtension
            ],
            onUpdate: () => {
                this.editorStateService.handleEditorUpdated(() => {
                    const value = this.getEditorValue();
                    this.onChange(value);
                    this.valueChange.emit(value);
                });
            },
            onSelectionUpdate: () => {
                this.editorStateService.handleEditorSelectionUpdated();
            },
            onBlur: () => {
                this.onTouched();
            }
        });

        this.editorStateService.handleEditorCreated();
    }

    public ngOnDestroy(): void {
        this.themeObserver?.disconnect();
        this.editorStateService.destroy();
        this.editor?.destroy();
    }

    @HostListener('document:mousedown', ['$event'])
    protected handleDocumentMouseDown(event: MouseEvent): void {
        this.editorStateService.handleDocumentMouseDown(event, this.hostElement.nativeElement);
    }

    @HostListener('document:keydown.escape', ['$event'])
    protected handleEscapeKey(event: Event): void {
        this.editorStateService.handleEscapeKey(event);
    }

    public writeValue(value: string | null): void {
        const normalizedValue = normalizeLegacyEditorHtml(value);
        this.pendingValue = normalizedValue;
        this.editorStateService.applyExternalValue(normalizedValue);
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.formDisabled.set(isDisabled);
    }

    public executeToolbarCommand(event: MouseEvent, command: ToolbarCommand): void {
        event.preventDefault();
        this.editorStateService.executeToolbarCommand(command);
    }

    public openLinkMenu(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.editorStateService.toggleLinkMenu(event);
    }

    public handleAttachmentInput(event: Event): void {
        this.editorStateService.closeMenus();
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (files?.length) {
            this.handleFiles(files);
            input.value = '';
        }
    }

    protected async handleSpellcheckWordClick(event: MouseEvent): Promise<void> {
        await this.editorStateService.handleSpellcheckWordClick(event);
    }

    protected closeLinkMenuFromAction(): void {
        this.editorStateService.closeLinkMenu(true);
    }

    protected applyLinkFromMenu(submission: LinkMenuSubmission): void {
        this.editorStateService.applyLinkFromMenu(submission);
    }

    protected removeLinkFromMenu(linkText: string): void {
        this.editorStateService.removeLinkFromMenu(linkText);
    }

    protected handleEditorSurfaceScroll(): void {
        this.editorStateService.closeMenus();
    }

    protected applySpellcheckSuggestion(suggestion: string): void {
        this.editorStateService.applySpellcheckSuggestion(suggestion);
    }

    private handleFiles(files: FileList): void {
        const fileArray = Array.from(files);
        const { errors } = FileNameValidationOperations.validateFiles(fileArray);

        if (errors.length > 0) {
            const firstError = errors[0];
            const message = this.translateService.instant(firstError.reason, firstError.params);
            this.snackbarService.warning(message);

            return;
        }

        this.attachments.emit(files);
    }

    private getEditorValue(): string {
        if (!this.editor) {
            return '';
        }

        const html = this.sanitizer.sanitize(SecurityContext.HTML, this.editor.getHTML()) ?? '';

        if (isMeaningfullyEmptyHtml(html)) {
            return '';
        }

        return html;
    }

    private resetEditorState(content: string): void {
        this.editorStateService.resetEditorState(content);
    }

    private syncToolbarState(): void {
        this.editorStateService.syncToolbarState();
    }
}
